import React, { useState } from "react"
import { Box, Button, Grid, makeStyles, Slide, Tooltip } from "@material-ui/core"
import queryString from "query-string"
import Config from "react-global-configuration"
import * as Sentry from "@sentry/browser"
import { useParams } from "react-router-dom"
import { useMutationCreateIntegration } from "../../data"
import { CreatorMaster } from "../Creators"
import { Icon } from "../Icon"
import { Caption } from "../Headings"
import { LoadingSpinner } from "../LoadingSpinner"
import { toId, useMountEffect } from "../../utils"
import { Blockquote } from "../Blockquotes"
import { IntegrationProviderInfo } from "./IntegrationProviderInfo"

const useStyles = makeStyles(() => ({
  contact: {
    textAlign: "center",
  },
}))

const IntegrationProviderConnector = ({ open, onClose, onConnected, integrationProvider }) => {
  const classes = useStyles()
  const [createIntegration] = useMutationCreateIntegration()
  const { integrationProviderId: id } = useParams()
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState(null)

  useMountEffect(() => {
    const qs = queryString.parse(window.location.search)
    if (qs.code) {
      setConnecting(true)
      const completeIntegration = async () => {
        try {
          const integration = await createIntegration({ variables: { integrationProvider: id, code: qs.code } })
          onConnected && onConnected(integration?.data.createIntegration || null)
        } catch (error) {
          Sentry.captureException(error)
          setError(error)
          setConnecting(false)
        }
      }
      completeIntegration()
    }
  })

  const handleConnect = () => {
    setConnecting(true)
    createConnectionRequest()
  }

  const createConnectionRequest = async () => {
    const clientBaseUri = Config.get("clientBaseUri")
    const { authoriseEndpoint, client: client_id, responseType: response_type, scope } = integrationProvider.connect

    const parameters = {
      client_id,
      response_type,
      scope,
      redirect_uri: `${clientBaseUri}/account/integrations/provider/${toId(integrationProvider)}/connect`,
    }

    window.location = `${authoriseEndpoint}?${queryString.stringify(parameters)}`
  }

  const handleClose = (update) => {
    onClose && onClose(update)
  }

  if (!integrationProvider) {
    return null
  }

  const { name, link, connect } = integrationProvider

  const form = (
    <Box mb={2}>
      {error && (
        <Slide in>
          <Box mb={2}>
            <Blockquote
              icon={<Icon name="warning" />}
              title="Failed to complete connection"
              body={`An error occurred while attempting to connect ${name} to your Operandio account. Our technicians are automatically notified of the error and will work to resolve it quickly. Please contact Operandio support through the help menu for any assistance in the mean time.`}
              color="warning"
            />
          </Box>
        </Slide>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <IntegrationProviderInfo integrationProvider={integrationProvider} />
        </Grid>
        <Grid item xs={12} md={4}>
          {connect && (
            <Tooltip title={`Connect to ${name} and authorise access from Operandio`}>
              <span>
                <Button variant="contained" color="primary" onClick={handleConnect} fullWidth disabled={connecting}>
                  {!connecting && <>Connect {name}</>}
                  {connecting && <LoadingSpinner size="24px" delay={false} />}
                </Button>
              </span>
            </Tooltip>
          )}
          {link && (
            <Box mt={connect ? 1 : 0}>
              <Tooltip title={`Visit external link: ${link}`}>
                <Button variant="contained" href={link} target="_blank" fullWidth>
                  <Icon name="external-link" />
                  <Box ml={1}>Visit website</Box>
                </Button>
              </Tooltip>
            </Box>
          )}
          {!connect && (
            <Box mt={1} justifyItems="center" className={classes.contact}>
              <Caption>Contact us for further details on enabling this integration</Caption>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  )

  return <CreatorMaster open={open} title={name} form={form} onClose={handleClose} />
}

export { IntegrationProviderConnector }
